import { useQuery } from "@tanstack/react-query";
import { Briefcase, Calendar, GraduationCap, Mail, Phone, User } from "lucide-react";
import { api, fetcher } from "@/lib/api";
import { ROLE_LABELS } from "@/lib/labels";

const Inisial = ({ nama, size = "md" }) => {
  const sizeClass = size === "lg" ? "h-12 w-12 text-sm" : "h-9 w-9 text-xs";
  return (
    <span className={`inline-flex ${sizeClass} shrink-0 items-center justify-center rounded-full bg-[#0B3D91] font-bold text-white dark:bg-[#3B82F6]`} aria-hidden>
      {(nama || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase()}
    </span>
  );
};

const InfoRow = ({ icon: Icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="truncate font-medium">{value}</p>
      </div>
    </div>
  );
};

export function StaffDetailCard({ membershipId, schoolId, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["feed-author", schoolId, membershipId],
    queryFn: () => fetcher(`/school/feed/author/${membershipId}`),
    enabled: !!membershipId,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="p-4 text-center text-xs text-muted-foreground">Memuat...</div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 text-center text-xs text-muted-foreground">Tidak dapat memuat profil</div>
    );
  }

  const staff = data;
  const isGuru = staff.role === "guru" || staff.role === "wali_kelas";
  const isWaliKelas = staff.role === "wali_kelas";

  return (
    <div className="w-72 p-3" data-testid="staff-detail-card">
      {/* Header */}
      <div className="flex items-start gap-2.5 pb-2.5 border-b">
        <Inisial nama={staff.full_name} size="lg" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold leading-tight">{staff.full_name || "Anggota sekolah"}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{ROLE_LABELS[staff.role] || staff.role}</p>
          {staff.status && (
            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${
              staff.status === "active" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              : "bg-muted text-muted-foreground"
            }`}>
              {staff.status === "active" ? "Aktif" : "Tidak aktif"}
            </span>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="mt-2.5 space-y-2">
        {staff.gender && (
          <InfoRow icon={User} label="Jenis Kelamin" value={staff.gender} />
        )}
        {staff.phone && (
          <InfoRow icon={Phone} label="Telepon" value={staff.phone} />
        )}
        {staff.email && (
          <InfoRow icon={Mail} label="Email" value={staff.email} />
        )}
        {staff.nip && (
          <InfoRow icon={Briefcase} label="NIP" value={staff.nip} />
        )}
        {staff.employee_registration_number && (
          <InfoRow icon={Briefcase} label="NUPTK/NITK" value={staff.employee_registration_number} />
        )}
        {staff.valid_from && (
          <InfoRow icon={Calendar} label="Bergabung Sejak" value={new Date(staff.valid_from).toLocaleDateString("id-ID", { year: "numeric", month: "long" })} />
        )}
      </div>

      {/* Mata Pelajaran (untuk guru) */}
      {isGuru && staff.subjects && staff.subjects.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t">
          <p className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            <GraduationCap className="h-3 w-3" aria-hidden /> Mata Pelajaran
          </p>
          <div className="flex flex-wrap gap-1">
            {staff.subjects.map((s, i) => (
              <span key={i} className="rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {s.code ? `${s.code} — ` : ""}{s.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Kelas Wali (untuk wali kelas) */}
      {isWaliKelas && staff.homeroom_classes && staff.homeroom_classes.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t">
          <p className="mb-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            <GraduationCap className="h-3 w-3" aria-hidden /> Wali Kelas
          </p>
          <div className="flex flex-wrap gap-1">
            {staff.homeroom_classes.map((c, i) => (
              <span key={i} className="rounded-md bg-purple-50 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                {c.name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
